// Workflow canvas → PNG dataUrl. Used by DoctorChatPopup to attach a
// snapshot of the FULL workflow (every node + every edge + the variables
// side-panel) on the first message of a chat session so the Doctor model
// has a visual of the whole graph, not just the cropped viewport the user
// happens to be looking at.
//
// Why "full" matters: the live canvas is `overflow:hidden` with a pan/zoom
// transform on `.canvas-zoom-layer`. Capturing the canvas DOM directly only
// gets the visible viewport (most of the workflow ends up off-screen in
// medium-large flows). Instead we:
//   1. Compute the world bbox of all nodes from `.nodes-layer > .node-position`
//      offset rects (these ignore the parent transform).
//   2. Render `.canvas-zoom-layer` via html-to-image with its transform
//      neutralized so EVERY node + edge is included at scale=1.
//   3. Render the `.vars-panel` separately (it's a sibling of the zoom
//      layer, not a child) and stitch it onto the same composite.
//   4. Resize the final composite down to the Anthropic token ceiling
//      (~1568×1024 ≈ 1.6 MP) — anything bigger gets binned to the same
//      ~1568-token charge anyway.
//
// All-or-nothing: any failure returns null so the chat send proceeds with
// no snapshot rather than blocking on a render error.

var htmlToImage = require('html-to-image');

// Token-ceiling cap — see Anthropic image-pricing table. Bigger images
// don't add fidelity for the model AND don't cost less in tokens.
var MAX_WIDTH_PX = 1568;
var MAX_HEIGHT_PX = 1024;

// Pad around the world bbox so nodes don't sit flush against the edge.
var BBOX_PAD_PX = 60;

// Dark-canvas background so transparent areas don't render white.
var BACKGROUND = '#0b1220';

// Gap between the variables panel and the graph in the composite.
var COMPOSITE_GAP_PX = 24;

// ---------------------------------------------------------------------------
// World bbox — union of every .node-position offset rect inside .nodes-layer.
// Returns null when there are no nodes (workflow is empty — nothing to shoot).
// ---------------------------------------------------------------------------

function computeWorldBBox(zoomLayer) {
  if (!zoomLayer) return null;
  var nodesLayer = zoomLayer.querySelector('.nodes-layer');
  if (!nodesLayer) return null;
  var nodes = nodesLayer.querySelectorAll('.node-position');
  if (!nodes || nodes.length === 0) return null;

  var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    var x = n.offsetLeft;
    var y = n.offsetTop;
    var w = n.offsetWidth || 200;
    var h = n.offsetHeight || 80;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  }
  if (!isFinite(minX)) return null;

  return {
    left: minX - BBOX_PAD_PX,
    top: minY - BBOX_PAD_PX,
    width: (maxX - minX) + BBOX_PAD_PX * 2,
    height: (maxY - minY) + BBOX_PAD_PX * 2
  };
}

// ---------------------------------------------------------------------------
// Capture helpers — each returns { dataUrl, width, height } or null.
// ---------------------------------------------------------------------------

// html-to-image clones the DOM and copies COMPUTED styles inline — but
// SVG <path> elements styled purely via stylesheet selectors (.conn-line)
// regularly come out invisible in the clone. Documented quirk; safest fix
// is to inline the stroke/fill attributes on every connection path before
// capture and remove them after. The mutation is invisible (same visual
// result) and only persists for the duration of the screenshot.
function inlineSvgStrokes(rootEl) {
  var paths = rootEl.querySelectorAll('.conn-line, .conn-line-future, .conn-line-custom');
  var touched = [];
  for (var i = 0; i < paths.length; i++) {
    var p = paths[i];
    var cs = window.getComputedStyle(p);
    // Snapshot whatever attrs we are about to override so we can restore.
    touched.push({
      el: p,
      stroke: p.getAttribute('stroke'),
      strokeWidth: p.getAttribute('stroke-width'),
      fill: p.getAttribute('fill'),
      strokeLinecap: p.getAttribute('stroke-linecap'),
      strokeLinejoin: p.getAttribute('stroke-linejoin')
    });
    p.setAttribute('stroke', cs.stroke || '#d3d3d3');
    p.setAttribute('stroke-width', cs.strokeWidth || '2.1');
    p.setAttribute('fill', cs.fill || 'none');
    p.setAttribute('stroke-linecap', cs.strokeLinecap || 'round');
    p.setAttribute('stroke-linejoin', cs.strokeLinejoin || 'round');
  }
  return touched;
}

function restoreSvgStrokes(touched) {
  for (var i = 0; i < touched.length; i++) {
    var t = touched[i];
    function restoreAttr(name, value) {
      if (value === null) t.el.removeAttribute(name);
      else t.el.setAttribute(name, value);
    }
    restoreAttr('stroke', t.stroke);
    restoreAttr('stroke-width', t.strokeWidth);
    restoreAttr('fill', t.fill);
    restoreAttr('stroke-linecap', t.strokeLinecap);
    restoreAttr('stroke-linejoin', t.strokeLinejoin);
  }
}

// Render the .canvas-zoom-layer at scale=1 with the world bbox aligned to
// (0, 0). We override its `transform` via html-to-image's `style` option so
// the live pan/zoom doesn't get baked into the snapshot — the user could
// be zoomed way in or panned far off, and we want the full graph regardless.
function captureZoomLayer(zoomLayer, bbox) {
  // Inline SVG stroke attrs so the connection lines survive the clone.
  // Restored in finally so a thrown render never leaves the live DOM dirty.
  var touched = inlineSvgStrokes(zoomLayer);
  return htmlToImage.toPng(zoomLayer, {
    width: bbox.width,
    height: bbox.height,
    cacheBust: true,
    backgroundColor: BACKGROUND,
    style: {
      transform: 'translate(' + (-bbox.left) + 'px, ' + (-bbox.top) + 'px)',
      transformOrigin: '0 0'
    }
  }).then(function(dataUrl) {
    restoreSvgStrokes(touched);
    if (!dataUrl) return null;
    return { dataUrl: dataUrl, width: bbox.width, height: bbox.height };
  }).catch(function(err) {
    restoreSvgStrokes(touched);
    throw err;
  });
}

function capturePanel(panelEl) {
  if (!panelEl) return Promise.resolve(null);
  var rect = panelEl.getBoundingClientRect();
  var w = Math.max(1, Math.round(rect.width));
  var h = Math.max(1, Math.round(rect.height));
  return htmlToImage.toPng(panelEl, {
    width: w,
    height: h,
    cacheBust: true,
    backgroundColor: BACKGROUND
  }).then(function(dataUrl) {
    if (!dataUrl) return null;
    return { dataUrl: dataUrl, width: w, height: h };
  }).catch(function() { return null; });
}

// ---------------------------------------------------------------------------
// Composite + downscale. The vars panel sits to the LEFT of the graph (same
// position the user sees on the live canvas). Final composite is resized
// down so it lands within the token-ceiling cap.
// ---------------------------------------------------------------------------

function loadImage(dataUrl) {
  return new Promise(function(resolve, reject) {
    var img = new Image();
    img.onload = function() { resolve(img); };
    img.onerror = function() { reject(new Error('image load failed')); };
    img.src = dataUrl;
  });
}

function pickScaleFactor(width, height) {
  var widthRatio = MAX_WIDTH_PX / width;
  var heightRatio = MAX_HEIGHT_PX / height;
  var ratio = Math.min(widthRatio, heightRatio, 1);
  // Floor at 0.4 — anything smaller and node labels become unreadable.
  return Math.max(ratio, 0.4);
}

function composite(graph, panel) {
  // Layout: panel on the LEFT (matches the live canvas), graph to its RIGHT.
  // If panel is missing, just use the graph.
  var panelWidth = panel ? panel.width + COMPOSITE_GAP_PX : 0;
  var panelHeight = panel ? panel.height : 0;
  var totalWidth = panelWidth + graph.width;
  var totalHeight = Math.max(panelHeight, graph.height);

  return Promise.all([
    loadImage(graph.dataUrl),
    panel ? loadImage(panel.dataUrl) : Promise.resolve(null)
  ]).then(function(imgs) {
    var graphImg = imgs[0];
    var panelImg = imgs[1];

    var scale = pickScaleFactor(totalWidth, totalHeight);
    var outWidth = Math.max(1, Math.round(totalWidth * scale));
    var outHeight = Math.max(1, Math.round(totalHeight * scale));

    var canvas = document.createElement('canvas');
    canvas.width = outWidth;
    canvas.height = outHeight;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(0, 0, outWidth, outHeight);

    if (panelImg) {
      ctx.drawImage(panelImg, 0, 0, panel.width * scale, panel.height * scale);
    }
    var graphX = panelWidth * scale;
    ctx.drawImage(graphImg, graphX, 0, graph.width * scale, graph.height * scale);

    return canvas.toDataURL('image/png');
  });
}

// ---------------------------------------------------------------------------
// Public entry — capture the full workflow as a single PNG dataUrl.
// Returns { dataUrl, mediaType, kind } or null on any failure.
// ---------------------------------------------------------------------------

function captureWorkflowCanvas(canvasEl) {
  if (!canvasEl) return Promise.resolve(null);

  var zoomLayer = canvasEl.querySelector('.canvas-zoom-layer');
  var bbox = computeWorldBBox(zoomLayer);
  if (!bbox) return Promise.resolve(null); // empty workflow, nothing to shoot

  var panelEl = canvasEl.querySelector('.vars-panel');

  return Promise.all([
    captureZoomLayer(zoomLayer, bbox),
    capturePanel(panelEl)
  ]).then(function(parts) {
    var graph = parts[0];
    var panel = parts[1];
    if (!graph) return null;
    return composite(graph, panel);
  }).then(function(dataUrl) {
    if (!dataUrl) return null;
    return {
      dataUrl: dataUrl,
      mediaType: 'image/png',
      kind: 'canvas-snapshot'
    };
  }).catch(function() {
    return null;
  });
}

module.exports = {
  captureWorkflowCanvas: captureWorkflowCanvas
};
